/**
 * The pure side of the «Approval rules» tab (masterplan credit-limits-v3, B5): who may edit, how the forms are read
 * and checked, which body a save sends, and the pick lists. The API checks every rule again (it is the authority);
 * these checks only spare a round trip and put the message next to the field. No React, no network.
 */
import type {
    ApproverUpdateBody, CreditLimitApprover, CreditLimitConfig, CreditLimitFinanceMember, CreditLimitPerson, CreditLimitSettings,
    CreditLimitTier, RequiredRoles, SettingsUpdateBody, TiersUpdateBody,
} from '../../../types/creditLimits';

/** Finance edits (E4), and only once migration 227 has made the tables (before it the defaults are shown). */
export function canEditRules(config: Pick<CreditLimitConfig, 'roles' | 'schemaReady'> | null | undefined): boolean {
    return !!config && config.schemaReady && config.roles.finance === true;
}

export const REQUIRED_ROLES_TEXT: Record<RequiredRoles, string> = {
    SM: 'Salesperson',
    BTM: 'BT manager',
    'BTM+TOP': 'BT manager and top level',
};

/** CHF with the Swiss apostrophe: 200000 -> «200'000». */
export function formatChfPlain(value: number): string {
    return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
}

/** A CHF amount typed by a person: digits, grouping with ' or a space, up to two decimals with a point; null if not. */
export function parseChfInput(text: string): number | null {
    const t = text.trim().replace(/['’\s]/g, '');
    if (!/^\d{1,10}(\.\d{1,2})?$/.test(t)) return null;
    const n = Number(t);
    return n > 0 ? n : null;
}

// ---- tiers

export interface TiersDraft {
    /** Upper limit of tier 1 and tier 2 as typed; tier 3 is open. */
    tier1: string;
    tier2: string;
}

export function tiersDraftOf(tiers: readonly CreditLimitTier[]): TiersDraft {
    const text = (n: number) => {
        const v = tiers.find(t => t.tierNo === n)?.maxAmountChf;
        return v === null || v === undefined ? '' : formatChfPlain(v);
    };
    return { tier1: text(1), tier2: text(2) };
}

export interface TiersCheck {
    tier1: string | null;
    tier2: string | null;
    /** Both amounts valid and rising. */
    ok: boolean;
    amounts: { tier1: number; tier2: number } | null;
}

export function checkTiersDraft(draft: TiersDraft): TiersCheck {
    const a = parseChfInput(draft.tier1);
    const b = parseChfInput(draft.tier2);
    const tier1 = a === null ? 'Enter an amount in CHF, e.g. 30000.' : null;
    let tier2 = b === null ? 'Enter an amount in CHF, e.g. 200000.' : null;
    if (a !== null && b !== null && !(a < b)) tier2 = 'Tier 2 must end above tier 1.';
    const ok = tier1 === null && tier2 === null;
    return { tier1, tier2, ok, amounts: ok ? { tier1: a!, tier2: b! } : null };
}

/** The body for `PUT config/tiers`: only the tiers whose amount differs from the saved ones; null when nothing does. */
export function tiersBody(saved: readonly CreditLimitTier[], draft: TiersDraft): TiersUpdateBody | null {
    const check = checkTiersDraft(draft);
    if (!check.ok || !check.amounts) return null;
    const now = new Map(saved.map(t => [t.tierNo, t.maxAmountChf]));
    const tiers: TiersUpdateBody['tiers'] = [];
    if (now.get(1) !== check.amounts.tier1) tiers.push({ tierNo: 1, maxAmountChf: check.amounts.tier1 });
    if (now.get(2) !== check.amounts.tier2) tiers.push({ tierNo: 2, maxAmountChf: check.amounts.tier2 });
    return tiers.length > 0 ? { tiers } : null;
}

/** «Up to CHF 30'000» / «Above CHF 200'000» for the table. */
export function tierRangeText(tiers: readonly CreditLimitTier[], tierNo: number): string {
    const sorted = [...tiers].sort((x, y) => x.tierNo - y.tierNo);
    const t = sorted.find(x => x.tierNo === tierNo);
    if (!t) return '–';
    const prev = sorted.filter(x => x.tierNo < tierNo).pop();
    if (t.maxAmountChf === null) return prev?.maxAmountChf != null ? `Above CHF ${formatChfPlain(prev.maxAmountChf)}` : 'Any amount';
    return `Up to CHF ${formatChfPlain(t.maxAmountChf)}`;
}

// ---- settings

export interface SettingsDraft {
    overdueRedDays: string;
    overdueAmberDays: string;
    nearLimitPct: string;
    defaultTopEmail: string;
}

export function settingsDraftOf(s: CreditLimitSettings): SettingsDraft {
    return {
        overdueRedDays: String(s.overdueRedDays),
        overdueAmberDays: String(s.overdueAmberDays),
        nearLimitPct: String(s.nearLimitPct),
        defaultTopEmail: (s.defaultTopEmail ?? '').toLowerCase(),
    };
}

const WHOLE = /^\d{1,3}$/;

export interface SettingsCheck {
    errors: Partial<Record<keyof SettingsDraft, string>>;
    ok: boolean;
}

export function checkSettingsDraft(draft: SettingsDraft): SettingsCheck {
    const errors: SettingsCheck['errors'] = {};
    const whole = (key: 'overdueRedDays' | 'overdueAmberDays' | 'nearLimitPct', min: number, max: number) => {
        const t = draft[key].trim();
        if (!WHOLE.test(t) || Number(t) < min || Number(t) > max) errors[key] = `A whole number from ${min} to ${max}.`;
    };
    whole('overdueRedDays', 1, 999);
    whole('overdueAmberDays', 1, 999);
    whole('nearLimitPct', 1, 100);
    if (!errors.overdueRedDays && !errors.overdueAmberDays && Number(draft.overdueAmberDays) > Number(draft.overdueRedDays)) {
        errors.overdueAmberDays = 'Amber must not start later than red.';
    }
    if (draft.defaultTopEmail.trim() === '') errors.defaultTopEmail = 'Choose a person.';
    return { errors, ok: Object.keys(errors).length === 0 };
}

/** The body for `PUT config/settings`: only the fields that differ from the saved settings; null when none does. */
export function settingsBody(saved: CreditLimitSettings, draft: SettingsDraft): SettingsUpdateBody | null {
    if (!checkSettingsDraft(draft).ok) return null;
    const body: SettingsUpdateBody = {};
    if (Number(draft.overdueRedDays) !== saved.overdueRedDays) body.overdueRedDays = Number(draft.overdueRedDays);
    if (Number(draft.overdueAmberDays) !== saved.overdueAmberDays) body.overdueAmberDays = Number(draft.overdueAmberDays);
    if (Number(draft.nearLimitPct) !== saved.nearLimitPct) body.nearLimitPct = Number(draft.nearLimitPct);
    if (draft.defaultTopEmail.trim().toLowerCase() !== (saved.defaultTopEmail ?? '').toLowerCase()) {
        body.defaultTopEmail = draft.defaultTopEmail.trim().toLowerCase();
    }
    return Object.keys(body).length > 0 ? body : null;
}

// ---- approvers and people

export interface PersonOption {
    email: string;
    label: string;
}

/**
 * The choices for a person field: the active employees by name, plus the current value when it is not among them (a
 * person who left stays visible and selectable only as what they are), so a row can be shown and saved untouched.
 */
export function personOptions(people: readonly CreditLimitPerson[] | undefined, names: Record<string, string> | undefined, current: string | null): PersonOption[] {
    const list = (people ?? [])
        .map(p => ({ email: p.email.toLowerCase(), label: p.name ? `${p.name} (${p.email})` : p.email }))
        .sort((a, b) => a.label.localeCompare(b.label, 'de-CH'));
    const cur = (current ?? '').trim().toLowerCase();
    if (cur !== '' && !list.some(o => o.email === cur)) {
        const name = names?.[cur];
        list.unshift({ email: cur, label: `${name ? `${name} (${cur})` : cur} – not active` });
    }
    return list;
}

export interface ApproverDraft {
    id: number | null;
    salesManagerName: string;
    smEmail: string;
    btmEmail: string;
    topEmail: string;
    isActive: boolean;
}

export function approverDraftOf(a: CreditLimitApprover | null, defaultTopEmail?: string | null): ApproverDraft {
    if (!a) return { id: null, salesManagerName: '', smEmail: '', btmEmail: '', topEmail: (defaultTopEmail ?? '').toLowerCase(), isActive: true };
    return {
        id: a.id, salesManagerName: a.salesManagerName, smEmail: (a.smEmail ?? '').toLowerCase(),
        btmEmail: (a.btmEmail ?? '').toLowerCase(), topEmail: (a.topEmail ?? '').toLowerCase(), isActive: a.isActive,
    };
}

export function checkApproverDraft(d: ApproverDraft): Partial<Record<'salesManagerName' | 'smEmail' | 'btmEmail' | 'topEmail', string>> {
    const errors: Partial<Record<'salesManagerName' | 'smEmail' | 'btmEmail' | 'topEmail', string>> = {};
    if (d.id === null && d.salesManagerName.trim() === '') errors.salesManagerName = 'Choose a sales manager.';
    if (d.smEmail === '') errors.smEmail = 'Choose a person.';
    if (d.btmEmail === '') errors.btmEmail = 'Choose a person.';
    if (d.topEmail === '') errors.topEmail = 'Choose a person.';
    return errors;
}

/** The body for `PUT config/approvers`; for an existing row only the fields that differ, null when none does. */
export function approverBody(saved: CreditLimitApprover | null, d: ApproverDraft): ApproverUpdateBody | null {
    if (Object.keys(checkApproverDraft(d)).length > 0) return null;
    if (!saved) {
        return { salesManagerName: d.salesManagerName, smEmail: d.smEmail, btmEmail: d.btmEmail, topEmail: d.topEmail, isActive: d.isActive };
    }
    const body: ApproverUpdateBody & { id: number } = { id: saved.id };
    if (d.smEmail !== (saved.smEmail ?? '').toLowerCase()) body.smEmail = d.smEmail;
    if (d.btmEmail !== (saved.btmEmail ?? '').toLowerCase()) body.btmEmail = d.btmEmail;
    if (d.topEmail !== (saved.topEmail ?? '').toLowerCase()) body.topEmail = d.topEmail;
    if (d.isActive !== saved.isActive) body.isActive = d.isActive;
    return Object.keys(body).length > 1 ? body : null;
}

/** Sales manager names that can still get a row: in the limit list, no row yet (any state). */
export function freeSalesManagers(options: readonly string[] | undefined, approvers: readonly CreditLimitApprover[]): string[] {
    const taken = new Set(approvers.map(a => a.salesManagerName.trim().toLowerCase()));
    return (options ?? []).filter(n => !taken.has(n.trim().toLowerCase()));
}

// ---- finance

/** The only active Finance person cannot be switched off (the API answers 409 too). */
export function isLastActiveFinance(finance: readonly CreditLimitFinanceMember[], email: string): boolean {
    const active = finance.filter(f => f.isActive);
    return active.length === 1 && active[0]!.email.toLowerCase() === email.toLowerCase();
}
